import { Hotkey } from "@tanstack/react-hotkeys";
import {
  COMBOBOX_REMOVE_LAST_MENTION_HOTKEY,
  SESSION_CLOSE_ARTIFACT_HOTKEY,
} from "@/domains/keyboard-shortcuts/shared-hotkeys";

type TabNumberShortcutIndex = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
type PinnedItemShortcutIndex = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export type ShortcutAction =
  | "create_note"
  | "create_note_voice"
  | "search"
  | "open_mem_it"
  | "show_heads_up"
  | "see_keyboard_shortcuts"
  | "organize_selected_notes"
  | "auto_organize_selected_notes"
  | "move_notes_to_trash"
  | "go_back"
  | "go_forward"
  | "go_home"
  | "go_collections"
  | "go_settings"
  | "new_tab"
  | "close_tab"
  | `go_to_tab_${TabNumberShortcutIndex}`
  | `go_to_pinned_item_${PinnedItemShortcutIndex}`
  | "escape"
  | "next_pane"
  | "toggle_mem_panel"
  | "toggle_sidebar"
  | "focus_mode"
  | "editor_find"
  | "editor_find_next"
  | "find_in_note_previous_result_alternate"
  | "open_note_in_split_view"
  | "move_note_to_other_split_view"
  | "cleanup_note"
  | "cleanup_and_auto_organize_note"
  | "copy_note_link"
  | "pin_note"
  | "quick_search_previous_result"
  | "quick_search_next_result"
  | "quick_search_select_highlighted_result"
  | "quick_search_close"
  | "notes_list_toggle_highlighted_selection"
  | "notes_list_extend_selection_to_highlighted"
  | "notes_list_highlight_previous"
  | "notes_list_highlight_previous_vim"
  | "notes_list_highlight_next"
  | "notes_list_highlight_next_vim"
  | "notes_list_open_highlighted"
  | "notes_list_open_highlighted_in_split_view"
  | "onboarding_continue"
  | "find_in_note_previous_result"
  | "find_in_note_next_result"
  | "find_in_note_next_result_arrow"
  | "find_in_note_next_result_enter"
  | "find_in_note_close"
  | "organize_dialog_focus_next_result"
  | "organize_dialog_focus_previous_result"
  | "organize_dialog_escape"
  | "combobox_remove_last_mention"
  | "image_viewer_close"
  | "image_viewer_previous_image"
  | "image_viewer_next_image"
  | "image_viewer_zoom_in"
  | "image_viewer_zoom_in_alternate"
  | "image_viewer_zoom_out"
  | "image_viewer_zoom_out_alternate"
  | "pdf_viewer_find"
  | "pdf_viewer_close"
  | "pdf_viewer_previous_page"
  | "pdf_viewer_next_page"
  | "pdf_viewer_zoom_in"
  | "pdf_viewer_zoom_in_alternate"
  | "pdf_viewer_zoom_out"
  | "pdf_viewer_zoom_out_alternate"
  | "pdf_find_next_result"
  | "pdf_find_previous_result"
  | "pdf_find_close"
  | "selected_text_add_link"
  | "session_close_artifact";

export type ShortcutBindingAction =
  | ShortcutAction
  | "focus_previous_result"
  | "focus_next_result"
  | "escape_to_close"
  | "viewer_previous"
  | "viewer_next"
  | "viewer_zoom_in"
  | "viewer_zoom_in_alternate"
  | "viewer_zoom_out"
  | "viewer_zoom_out_alternate";

export type ShortcutPlatformBucket = "apple" | "pc";

export type HotkeyByPlatform = {
  apple: Hotkey;
  pc: Hotkey;
};

export type ShortcutBrowserFallback = true | Partial<HotkeyByPlatform>;

export type ShortcutSection =
  | "essentials"
  | "navigation"
  | "notes_list"
  | "editing"
  | "with_text_selected";

type ShortcutBindingSingle = {
  hotkey: Hotkey;
  browserFallback?: ShortcutBrowserFallback;
  hotkeyByPlatform?: never;
};

type ShortcutBindingSplit = {
  hotkeyByPlatform: HotkeyByPlatform;
  browserFallback?: ShortcutBrowserFallback;
  hotkey?: never;
};

export type ShortcutBinding = ShortcutBindingSingle | ShortcutBindingSplit;

type ShortcutRegistryEntry = {
  action: ShortcutAction;
  bindingAction?: ShortcutBindingAction;
  label: string;
  section: ShortcutSection;
  contextLabel?: string;
  searchHint?: string;
} & ShortcutBinding;

type ShortcutSectionConfig = {
  section: ShortcutSection;
  title: string;
};

export type KeyboardShortcutModalItem = {
  id: string;
  action: ShortcutAction;
  bindingAction: ShortcutBindingAction;
  label: string;
  binding: ShortcutBinding;
  contextLabel?: string;
  displayHotkeySuffix?: string;
  searchHint?: string;
};

export type KeyboardShortcutModalSection = {
  section: ShortcutSection;
  title: string;
  shortcuts: KeyboardShortcutModalItem[];
};

const KEYBOARD_SHORTCUT_SECTION_CONFIG: readonly ShortcutSectionConfig[] = [
  { section: "essentials", title: "Essentials" },
  { section: "navigation", title: "Navigation" },
  { section: "notes_list", title: "Notes list" },
  { section: "editing", title: "Editing" },
  { section: "with_text_selected", title: "With text selected" },
];

const TAB_MODAL_SHORTCUT_ACTIONS = ([1, 2, 3, 4, 5, 6, 7, 8, 9] as const).map(
  index => `go_to_tab_${index}` as const
);

const PINNED_ITEM_MODAL_SHORTCUT_ACTIONS = ([1, 2, 3, 4, 5, 6, 7, 8, 9] as const).map(
  index => `go_to_pinned_item_${index}` as const
);

const TAB_NUMBER_SHORTCUTS: readonly ShortcutRegistryEntry[] = (
  [1, 2, 3, 4, 5, 6, 7, 8, 9] as const
).map(index => ({
  action: `go_to_tab_${index}`,
  label: `Go to tab ${index}`,
  section: "navigation",
  hotkey: `Mod+${index}` as Hotkey,
  browserFallback: true,
  searchHint: `switch to tab ${index}, tab ${index}`,
}));

const PINNED_ITEM_SHORTCUTS: readonly ShortcutRegistryEntry[] = (
  [1, 2, 3, 4, 5, 6, 7, 8, 9] as const
).map(index => ({
  action: `go_to_pinned_item_${index}`,
  label: `Open pinned item ${index}`,
  section: "navigation",
  hotkeyByPlatform: {
    apple: `Mod+Control+${index}` as Hotkey,
    pc: `Control+Shift+${index}` as Hotkey,
  },
  searchHint: `open pinned item ${index}, pinned ${index}, sidebar pinned item ${index}`,
}));

const KEYBOARD_SHORTCUT_REGISTRY: readonly ShortcutRegistryEntry[] = [
  {
    action: "create_note",
    label: "New note",
    section: "essentials",
    hotkey: "Mod+N",
    browserFallback: true,
  },
  {
    action: "create_note_voice",
    label: "New note and start voice recording",
    section: "essentials",
    hotkey: "Mod+Shift+N",
    browserFallback: true,
    searchHint: "voice note, start voice recording, new note with voice",
  },
  {
    action: "search",
    label: "Search",
    section: "essentials",
    hotkey: "Mod+K",
  },
  {
    action: "open_mem_it",
    label: "Open Mem It",
    section: "essentials",
    hotkey: "Mod+J",
    searchHint: "chat, Go to Mem It, attach selected notes, attach selected collections",
  },
  {
    action: "show_heads_up",
    label: "Show Heads Up",
    section: "essentials",
    hotkey: "Mod+Shift+J",
    searchHint: "related notes, connections, side panel, right panel",
  },
  {
    action: "see_keyboard_shortcuts",
    label: "See keyboard shortcuts",
    section: "essentials",
    hotkey: "Mod+/",
    searchHint: "keyboard shortcut modal, shortcuts help",
  },
  {
    action: "organize_selected_notes",
    label: "Organize note(s)",
    section: "essentials",
    hotkey: "Mod+O",
    searchHint: "collections, add to collection, selected notes, current note",
  },
  {
    action: "auto_organize_selected_notes",
    label: "Auto Organize note(s)",
    section: "essentials",
    hotkey: "Mod+Shift+O",
    browserFallback: {
      pc: "Alt+O",
    },
    searchHint: "automatic organize, AI organize, selected notes, current note",
  },
  {
    action: "move_notes_to_trash",
    label: "Move note(s) to trash",
    section: "essentials",
    hotkey: "Mod+Shift+Backspace",
    searchHint: "trash, move to trash, delete selected notes, delete active note",
  },
  {
    action: "quick_search_previous_result",
    bindingAction: "focus_previous_result",
    label: "Move to previous result",
    section: "essentials",
    contextLabel: "Quick search",
    hotkey: "ArrowUp",
    searchHint: "quick search previous result, search modal previous result",
  },
  {
    action: "quick_search_next_result",
    bindingAction: "focus_next_result",
    label: "Move to next result",
    section: "essentials",
    contextLabel: "Quick search",
    hotkey: "ArrowDown",
    searchHint: "quick search next result, search modal next result",
  },
  {
    action: "quick_search_select_highlighted_result",
    label: "Open highlighted result",
    section: "essentials",
    contextLabel: "Quick search",
    hotkey: "Enter",
    searchHint: "quick search open result, search modal open result",
  },
  {
    action: "quick_search_close",
    bindingAction: "escape_to_close",
    label: "Close search",
    section: "essentials",
    contextLabel: "Quick search",
    hotkey: "Escape",
    searchHint: "quick search close, search modal close",
  },
  {
    action: "onboarding_continue",
    label: "Continue",
    section: "essentials",
    contextLabel: "Onboarding",
    hotkey: "Enter",
    searchHint: "next onboarding step",
  },
  {
    action: "go_back",
    label: "Go back",
    section: "navigation",
    hotkeyByPlatform: {
      apple: "Mod+[",
      pc: "Alt+ArrowLeft",
    },
    browserFallback: {
      pc: "Control+ArrowLeft",
    },
  },
  {
    action: "go_forward",
    label: "Go forward",
    section: "navigation",
    hotkeyByPlatform: {
      apple: "Mod+]",
      pc: "Alt+ArrowRight",
    },
    browserFallback: {
      pc: "Control+ArrowRight",
    },
  },
  {
    action: "go_home",
    label: "Go to Home",
    section: "navigation",
    hotkey: "Mod+Shift+H",
  },
  {
    action: "go_collections",
    label: "Go to Collections",
    section: "navigation",
    hotkey: "Mod+Shift+C",
  },
  {
    action: "go_settings",
    label: "Go to Settings",
    section: "navigation",
    hotkey: "Mod+Shift+S",
  },
  {
    action: "new_tab",
    label: "New tab",
    section: "navigation",
    hotkey: "Mod+T",
    browserFallback: true,
  },
  {
    action: "close_tab",
    label: "Close tab",
    section: "navigation",
    hotkey: "Mod+W",
    browserFallback: true,
  },
  ...TAB_NUMBER_SHORTCUTS,
  ...PINNED_ITEM_SHORTCUTS,
  {
    action: "escape",
    label: "Escape",
    section: "navigation",
    hotkey: "Escape",
  },
  {
    action: "next_pane",
    label: "Next pane",
    section: "navigation",
    hotkey: "Mod+.",
    searchHint: "next split pane, switch pane, cycle panes",
  },
  {
    action: "toggle_mem_panel",
    label: "Open or close Mem panel",
    section: "navigation",
    hotkey: "Mod+\\" as Hotkey,
    searchHint: "right sidebar, side panel, Mem It, heads up",
  },
  {
    action: "toggle_sidebar",
    label: "Open or close sidebar",
    section: "navigation",
    hotkey: "Mod+Shift+\\" as Hotkey,
    searchHint: "left sidebar, navigation sidebar",
  },
  {
    action: "focus_mode",
    label: "Focus mode",
    section: "navigation",
    hotkeyByPlatform: {
      apple: "Mod+Control+\\" as Hotkey,
      pc: "Control+Alt+\\" as Hotkey,
    },
    searchHint: "toggle both sidebars, hide sidebars, show sidebars",
  },
  {
    action: "open_note_in_split_view",
    label: "Open note in split view",
    section: "navigation",
    hotkey: "Mod+Enter",
    searchHint: "inactive pane, split pane, second pane",
  },
  {
    action: "move_note_to_other_split_view",
    label: "Move note to other split pane",
    section: "navigation",
    hotkey: "Mod+Alt+T",
    searchHint: "move note to other pane, split pane, inactive pane",
  },
  {
    action: "image_viewer_close",
    bindingAction: "escape_to_close",
    label: "Close viewer",
    section: "navigation",
    contextLabel: "Image viewer",
    hotkey: "Escape",
  },
  {
    action: "image_viewer_previous_image",
    bindingAction: "viewer_previous",
    label: "Previous image",
    section: "navigation",
    contextLabel: "Image viewer",
    hotkey: "ArrowLeft",
  },
  {
    action: "image_viewer_next_image",
    bindingAction: "viewer_next",
    label: "Next image",
    section: "navigation",
    contextLabel: "Image viewer",
    hotkey: "ArrowRight",
  },
  {
    action: "image_viewer_zoom_in",
    bindingAction: "viewer_zoom_in",
    label: "Zoom in",
    section: "navigation",
    contextLabel: "Image viewer",
    hotkey: "=",
  },
  {
    action: "image_viewer_zoom_in_alternate",
    bindingAction: "viewer_zoom_in_alternate",
    label: "Zoom in",
    section: "navigation",
    contextLabel: "Image viewer",
    hotkey: "Mod+=",
  },
  {
    action: "image_viewer_zoom_out",
    bindingAction: "viewer_zoom_out",
    label: "Zoom out",
    section: "navigation",
    contextLabel: "Image viewer",
    hotkey: "-",
  },
  {
    action: "image_viewer_zoom_out_alternate",
    bindingAction: "viewer_zoom_out_alternate",
    label: "Zoom out",
    section: "navigation",
    contextLabel: "Image viewer",
    hotkey: "Mod+-",
  },
  {
    action: "pdf_viewer_find",
    label: "Find in PDF",
    section: "navigation",
    contextLabel: "PDF viewer",
    hotkey: "Mod+F",
  },
  {
    action: "pdf_viewer_close",
    bindingAction: "escape_to_close",
    label: "Close viewer",
    section: "navigation",
    contextLabel: "PDF viewer",
    hotkey: "Escape",
  },
  {
    action: "pdf_viewer_previous_page",
    bindingAction: "viewer_previous",
    label: "Previous page",
    section: "navigation",
    contextLabel: "PDF viewer",
    hotkey: "ArrowLeft",
  },
  {
    action: "pdf_viewer_next_page",
    bindingAction: "viewer_next",
    label: "Next page",
    section: "navigation",
    contextLabel: "PDF viewer",
    hotkey: "ArrowRight",
  },
  {
    action: "pdf_viewer_zoom_in",
    bindingAction: "viewer_zoom_in",
    label: "Zoom in",
    section: "navigation",
    contextLabel: "PDF viewer",
    hotkey: "=",
  },
  {
    action: "pdf_viewer_zoom_in_alternate",
    bindingAction: "viewer_zoom_in_alternate",
    label: "Zoom in",
    section: "navigation",
    contextLabel: "PDF viewer",
    hotkey: "Mod+=",
  },
  {
    action: "pdf_viewer_zoom_out",
    bindingAction: "viewer_zoom_out",
    label: "Zoom out",
    section: "navigation",
    contextLabel: "PDF viewer",
    hotkey: "-",
  },
  {
    action: "pdf_viewer_zoom_out_alternate",
    bindingAction: "viewer_zoom_out_alternate",
    label: "Zoom out",
    section: "navigation",
    contextLabel: "PDF viewer",
    hotkey: "Mod+-",
  },
  {
    action: "session_close_artifact",
    label: "Close artifact",
    section: "navigation",
    contextLabel: "Session artifact",
    hotkey: SESSION_CLOSE_ARTIFACT_HOTKEY,
  },
  {
    action: "notes_list_toggle_highlighted_selection",
    label: "Toggle highlighted item selection",
    section: "notes_list",
    hotkey: "X",
    searchHint: "notes list select highlighted item",
  },
  {
    action: "notes_list_extend_selection_to_highlighted",
    label: "Extend selection to highlighted item",
    section: "notes_list",
    hotkey: "Shift+X",
    searchHint: "notes list extend selection",
  },
  {
    action: "notes_list_highlight_previous",
    bindingAction: "focus_previous_result",
    label: "Highlight previous item",
    section: "notes_list",
    hotkey: "ArrowUp",
  },
  {
    action: "notes_list_highlight_previous_vim",
    label: "Highlight previous item",
    section: "notes_list",
    hotkey: "K",
    searchHint: "vim up, list previous item",
  },
  {
    action: "notes_list_highlight_next",
    bindingAction: "focus_next_result",
    label: "Highlight next item",
    section: "notes_list",
    hotkey: "ArrowDown",
  },
  {
    action: "notes_list_highlight_next_vim",
    label: "Highlight next item",
    section: "notes_list",
    hotkey: "J",
    searchHint: "vim down, list next item",
  },
  {
    action: "notes_list_open_highlighted",
    label: "Open highlighted item",
    section: "notes_list",
    hotkey: "Enter",
  },
  {
    action: "notes_list_open_highlighted_in_split_view",
    label: "Open highlighted item in split view",
    section: "notes_list",
    hotkey: "Mod+Enter",
  },
  {
    action: "editor_find",
    label: "Find",
    section: "editing",
    hotkey: "Mod+F",
  },
  {
    action: "editor_find_next",
    label: "Find next",
    section: "editing",
    hotkey: "Mod+G",
  },
  {
    action: "find_in_note_previous_result_alternate",
    label: "Find previous result",
    section: "editing",
    contextLabel: "Find in note",
    hotkey: "Mod+E",
    searchHint: "find previous result, reverse search",
  },
  {
    action: "cleanup_note",
    label: "Clean up note",
    section: "editing",
    hotkey: "Mod+Shift+U",
    searchHint: "cleanup note, refine note, fix note",
  },
  {
    action: "cleanup_and_auto_organize_note",
    label: "Clean up and Auto Organize note",
    section: "editing",
    hotkey: "Mod+Shift+M",
    searchHint: "cleanup and organize note, refine and organize note",
  },
  {
    action: "copy_note_link",
    label: "Copy note link",
    section: "editing",
    hotkey: "Mod+L",
    browserFallback: true,
    searchHint: "copy link, share note link, note URL",
  },
  {
    action: "pin_note",
    label: "Pin note",
    section: "editing",
    hotkey: "Mod+Shift+P",
    searchHint: "favorite note, pin to sidebar, unpin note",
  },
  {
    action: "find_in_note_previous_result",
    bindingAction: "focus_previous_result",
    label: "Find previous result",
    section: "editing",
    contextLabel: "Find in note",
    hotkey: "ArrowUp",
  },
  {
    action: "find_in_note_next_result",
    label: "Find next result",
    section: "editing",
    contextLabel: "Find in note",
    hotkey: "Mod+G",
  },
  {
    action: "find_in_note_next_result_arrow",
    bindingAction: "focus_next_result",
    label: "Find next result",
    section: "editing",
    contextLabel: "Find in note",
    hotkey: "ArrowDown",
  },
  {
    action: "find_in_note_next_result_enter",
    label: "Find next result",
    section: "editing",
    contextLabel: "Find in note",
    hotkey: "Enter",
  },
  {
    action: "find_in_note_close",
    bindingAction: "escape_to_close",
    label: "Close find",
    section: "editing",
    contextLabel: "Find in note",
    hotkey: "Escape",
  },
  {
    action: "organize_dialog_focus_next_result",
    bindingAction: "focus_next_result",
    label: "Move focus to next result",
    section: "editing",
    contextLabel: "Organize dialog",
    hotkey: "ArrowDown",
  },
  {
    action: "organize_dialog_focus_previous_result",
    bindingAction: "focus_previous_result",
    label: "Move focus to previous result",
    section: "editing",
    contextLabel: "Organize dialog",
    hotkey: "ArrowUp",
  },
  {
    action: "organize_dialog_escape",
    bindingAction: "escape_to_close",
    label: "Close or step back",
    section: "editing",
    contextLabel: "Organize dialog",
    hotkey: "Escape",
  },
  {
    action: "pdf_find_next_result",
    label: "Find next result",
    section: "editing",
    contextLabel: "Find in PDF",
    hotkey: "Enter",
  },
  {
    action: "pdf_find_previous_result",
    label: "Find previous result",
    section: "editing",
    contextLabel: "Find in PDF",
    hotkey: "Shift+Enter",
  },
  {
    action: "pdf_find_close",
    bindingAction: "escape_to_close",
    label: "Close find",
    section: "editing",
    contextLabel: "Find in PDF",
    hotkey: "Escape",
  },
  {
    action: "combobox_remove_last_mention",
    label: "Remove last mention",
    section: "editing",
    contextLabel: "Combobox input",
    hotkey: COMBOBOX_REMOVE_LAST_MENTION_HOTKEY,
  },
  {
    action: "selected_text_add_link",
    label: "Add link",
    section: "with_text_selected",
    contextLabel: "Text selected",
    hotkey: "Mod+K",
    searchHint: "add link, edit link, selected text link",
  },
];

const toShortcutBinding = (entry: ShortcutRegistryEntry): ShortcutBinding => {
  if (entry.hotkeyByPlatform !== undefined) {
    return {
      hotkeyByPlatform: entry.hotkeyByPlatform,
      browserFallback: entry.browserFallback,
    };
  }

  return {
    hotkey: entry.hotkey,
    browserFallback: entry.browserFallback,
  };
};

const getShortcutBindingAction = (entry: ShortcutRegistryEntry): ShortcutBindingAction =>
  entry.bindingAction ?? entry.action;

export const KEYBOARD_SHORTCUT_BINDINGS = KEYBOARD_SHORTCUT_REGISTRY.reduce<
  Record<ShortcutBindingAction, ShortcutBinding>
>(
  (bindings, entry) => {
    const bindingAction = getShortcutBindingAction(entry);
    if (bindings[bindingAction] !== undefined) {
      return bindings;
    }

    return {
      ...bindings,
      [bindingAction]: toShortcutBinding(entry),
    };
  },
  {} as Record<ShortcutBindingAction, ShortcutBinding>
);

export const getShortcutBinding = (
  action: ShortcutAction | ShortcutBindingAction
): ShortcutBinding | undefined => {
  const directBinding = KEYBOARD_SHORTCUT_BINDINGS[action as ShortcutBindingAction];
  if (directBinding) {
    return directBinding;
  }

  const entry = KEYBOARD_SHORTCUT_REGISTRY.find(shortcut => shortcut.action === action);
  if (!entry) {
    return undefined;
  }

  return KEYBOARD_SHORTCUT_BINDINGS[getShortcutBindingAction(entry)];
};

export const getKeyboardShortcutModalSections = (): KeyboardShortcutModalSection[] => {
  const sectionMap = new Map<ShortcutSection, KeyboardShortcutModalSection>(
    KEYBOARD_SHORTCUT_SECTION_CONFIG.map(({ section, title }) => [
      section,
      { section, title, shortcuts: [] },
    ])
  );

  for (const entry of KEYBOARD_SHORTCUT_REGISTRY) {
    const section = sectionMap.get(entry.section);
    if (!section) {
      continue;
    }

    section.shortcuts.push({
      id: entry.action,
      action: entry.action,
      bindingAction: getShortcutBindingAction(entry),
      label: entry.label,
      binding: toShortcutBinding(entry),
      contextLabel: entry.contextLabel,
      searchHint: entry.searchHint,
    });
  }

  return KEYBOARD_SHORTCUT_SECTION_CONFIG.flatMap(({ section }) => {
    const modalSection = sectionMap.get(section);
    if (!modalSection || modalSection.shortcuts.length === 0) {
      return [];
    }

    return [collapseModalShortcutRanges(modalSection)];
  });
};

const collapseModalShortcutRanges = (
  modalSection: KeyboardShortcutModalSection
): KeyboardShortcutModalSection => {
  if (modalSection.section !== "navigation") {
    return modalSection;
  }

  const shortcutsByAction = new Map(
    modalSection.shortcuts.map(shortcut => [shortcut.action, shortcut] as const)
  );
  const groupedShortcuts = new Map<ShortcutAction, KeyboardShortcutModalItem>([
    [
      TAB_MODAL_SHORTCUT_ACTIONS[0],
      {
        ...shortcutsByAction.get(TAB_MODAL_SHORTCUT_ACTIONS[0])!,
        id: "go_to_tab_1_through_9",
        label: "Go to tab 1 - 9",
        displayHotkeySuffix: "-9",
        searchHint: "go to tab 1 - 9, switch tabs by number, tab 1, tab 9",
      },
    ],
    [
      PINNED_ITEM_MODAL_SHORTCUT_ACTIONS[0],
      {
        ...shortcutsByAction.get(PINNED_ITEM_MODAL_SHORTCUT_ACTIONS[0])!,
        id: "go_to_pinned_item_1_through_9",
        label: "Open pinned item 1 - 9",
        displayHotkeySuffix: "-9",
        searchHint: "open pinned item 1 - 9, pinned item 1, pinned item 9",
      },
    ],
  ]);
  const collapsedActions = new Set<ShortcutAction>([
    ...TAB_MODAL_SHORTCUT_ACTIONS.slice(1),
    ...PINNED_ITEM_MODAL_SHORTCUT_ACTIONS.slice(1),
  ]);

  return {
    ...modalSection,
    shortcuts: modalSection.shortcuts.flatMap(shortcut => {
      if (collapsedActions.has(shortcut.action)) {
        return [];
      }

      return [groupedShortcuts.get(shortcut.action) ?? shortcut];
    }),
  };
};
